
import { HttpClient } from '@angular/common/http';
import { Injectable, DOCUMENT, inject } from '@angular/core';
import { OrderEnum, PaginationResponseDto, SearchRequestDto } from '@mogenius/client-sdk/core';
import { environment } from '@mogenius/environments/environment';
import {
  GitAccessTokenCreateRequestDto,
  GitBranchDto,
  GitConnectionTypeEnum,
  GitRepositoryDto,
} from '@mogenius/client-sdk/core-dto';
import { BaseSearchService } from '@mogenius/shared/services/base-search-service';
import { NotificationService } from '@mogenius/shared/services/notification.service';
import { plainToInstance } from 'class-transformer';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export const DEFAULT_GIT_SERVICE_SEARCH_REQUEST: SearchRequestDto = {
  searchString: '',
  page: 1,
  limit: 100,
  orderBy: 'name',
  order: OrderEnum.DESC
};
export const DEFAULT_GIT_SERVICE_SEARCH_RESPONSE: PaginationResponseDto<GitRepositoryDto> = plainToInstance(
  class extends PaginationResponseDto<GitRepositoryDto> {},
  {
    totalCount: 0,
    page: 0,
    limit: DEFAULT_GIT_SERVICE_SEARCH_REQUEST.limit,
    maxPage: 0,
    prevPage: 0,
    nextPage: 0,
    data: []
  }
);

@Injectable({
  providedIn: 'root'
})
export class GitBaseService extends BaseSearchService<
  GitRepositoryDto,
  {
    type: GitConnectionTypeEnum;
    username: string;
    password: string;
  }
> {
  protected readonly _http = inject(HttpClient);
  protected readonly _notificationService = inject(NotificationService);
  protected readonly _document = inject<Document>(DOCUMENT);

  protected _apiBase: any = environment.githubService;

  protected _searchRequest$: BehaviorSubject<SearchRequestDto> = new BehaviorSubject<SearchRequestDto>(
    DEFAULT_GIT_SERVICE_SEARCH_REQUEST
  );
  protected _searchResponse$: BehaviorSubject<PaginationResponseDto<GitRepositoryDto>> = new BehaviorSubject<
    PaginationResponseDto<GitRepositoryDto>
  >(DEFAULT_GIT_SERVICE_SEARCH_RESPONSE);

  public search(
    searchRequest?: SearchRequestDto,
    hideLoadingSpinner?: boolean,
    paginatedData?: boolean,
    gitConnection?: {
      type: GitConnectionTypeEnum;
      username: string;
      password: string;
    }
  ): Observable<PaginationResponseDto<GitRepositoryDto>> {
    if (!searchRequest) {
      searchRequest = DEFAULT_GIT_SERVICE_SEARCH_REQUEST;
    }

    const endpoint = this.getEndpoint(this._apiBase.repositories);

    const headers: { [key: string]: string } = {
      'Content-Type': `${endpoint.header.contentType}`
    };
    if (gitConnection) {
      headers['git-credentials'] = btoa(JSON.stringify(gitConnection));
    }

    return this._http
      .request<PaginationResponseDto<GitRepositoryDto>>(endpoint.method, endpoint.url, {
        headers: headers,
        params: this.getSearchParam(searchRequest)
      })
      .pipe(
        map((res) => {
          res.data = res.data.filter((r) => r !== null);
          res.data = res.data.map((item: GitRepositoryDto) =>
            plainToInstance(GitRepositoryDto, item, { excludeExtraneousValues: true })
          );
          return res;
        }),
        tap((response: PaginationResponseDto<GitRepositoryDto>) => {
          const cache = _.cloneDeep(response);
          if (searchRequest?.page && searchRequest?.page > 1) {
            cache.data = [...this._searchResponse$.getValue().data, ...cache.data];
          } else {
            cache.data = cache.data;
          }
          this._searchResponse$.next(cache);
        })
      );
  }

  public authorizePublicLink(): Observable<string> {
    const endpoint = this.getEndpoint(this._apiBase.authorizePublicLink);
    return this._http.request(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header.contentType,
        'x-callback-url': `${window.location.href}`
      },
      responseType: 'text'
    });
  }

  public appConnectionAuthLink(): Observable<string> {
    const endpoint = this.getEndpoint(this._apiBase.appConnectionAuthLink);
    return this._http.request(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header.contentType,
        'x-callback-url': `${window.location.href}`
      },
      responseType: 'text'
    });
  }

  public branchList(
    cloneUrl: string,
    gitConnection?: {
      type: GitConnectionTypeEnum;
      username: string;
      password: string;
      gitCustomDomain?: string;
    },
    page = 1
  ): Observable<PaginationResponseDto<GitBranchDto>> {
    const endpoint = this.getEndpoint(this._apiBase.branchList);
    const params = this.getSearchParam({
      searchString: '',
      page: page,
      limit: 100,
      orderBy: 'name',
      order: OrderEnum.DESC
    });

    const headers: { [key: string]: string } = {
      'Content-Type': `${endpoint.header.contentType}`,
      hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
    };
    if (gitConnection) {
      headers['git-credentials'] = btoa(JSON.stringify(gitConnection));
    }

    return this._http
      .request<PaginationResponseDto<GitBranchDto>>(endpoint.method, endpoint.url, {
        headers: headers,
        params: {
          cloneUrl: cloneUrl,
          ...params
        }
      })
      .pipe(
        map((response: PaginationResponseDto<GitBranchDto>) => {
          response = plainToInstance(PaginationResponseDto<GitBranchDto>, response, {
            excludeExtraneousValues: true
          });
          response.data = response.data.map((item) =>
            plainToInstance(GitBranchDto, item, { excludeExtraneousValues: true })
          );
          return response;
        })
      );
  }

  public accessToken(body: GitAccessTokenCreateRequestDto): Observable<void> {
    const endpoint = this.getEndpoint(this._apiBase.accessToken);
    return this._http.request<void>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header.contentType,
        hideLoadingSpinner: '1' // hideLoadingSpinner ? '1' : '0'
      },
      body: body
    });
  }

  public delete(): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.delete);
    return this._http.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header.contentType
      }
    });
  }

  public cleanUp(): void {
    this._searchRequest$.next(DEFAULT_GIT_SERVICE_SEARCH_REQUEST);
    this._searchResponse$.next(DEFAULT_GIT_SERVICE_SEARCH_RESPONSE);
  }
}
